import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Avatar,
  Divider,
  Badge,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Box,
  Link,
  LinearProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
  Public as PublicIcon,
  Language as LanguageIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Error as ErrorIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Link as LinkIcon,
  OpenInNew as OpenInNewIcon,
  Analytics as AnalyticsIcon,
  Group as GroupIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import Logo from '../common/Logo';
import { getPressReleases, PressReleaseResponse } from '../../services/prApi';

interface Publication {
  id: string;
  title: string;
  url: string;
  domain: string;
  status: 'published' | 'pending' | 'rejected' | 'scheduled';
  publishedAt: string;
  prTitle: string;
  prId: string;
  channel: string;
  tier: 'tier1' | 'tier2' | 'tier3' | 'tier4';
  industry: string;
  location: string;
  metrics: {
    views: number;
    clicks: number;
    shares: number;
    comments: number;
    engagement: number;
  };
  author: string;
  tags: string[];
  isFavorite: boolean;
  lastUpdated: string;
}

const Publications: React.FC = () => {
  const navigate = useNavigate();
  const [publications, setPublications] = useState<Publication[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [industryFilter, setIndustryFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPublication, setSelectedPublication] = useState<Publication | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);


  useEffect(() => {
    const fetchPublications = async () => {
      try {
        setLoading(true);
        setError(null);
        setLoadingProgress(0);
        setPublications([]);

        // Fetch PRs - this should include analytics.publications data
        const pressReleases: PressReleaseResponse[] = await getPressReleases();
        setLoadingProgress(50);

        // Filter to only published/distributed PRs
        const publishedPRs = pressReleases.filter(
          pr => pr.status === 'published' || pr.status === 'distributed'
        );

        const allPublications: Publication[] = [];

        // Use analytics data directly from PR response (much faster!)
        publishedPRs.forEach((pr) => {
          // Check if PR has analytics.publications data
          const publications = pr.analytics?.publications || [];
          
          if (publications.length > 0) {
            // Use data directly from PR response
            publications.forEach((pub, index) => {
              let domain = '';
              if (pub.url) {
                try {
                  const urlObj = new URL(pub.url);
                  domain = urlObj.hostname.replace('www.', '');
                } catch {
                  domain = pub.url;
                }
              }

              const views = pub.impressions || 0;
              const clicks = pub.clicks || 0;
              const shares = pub.shares || 0;
              const engagement = views > 0 ? (clicks / views) * 100 : 0;

              // Get tier from publication data, defaulting to tier1 if not set (matching backend default)
              const pubTier = pub.tier || 1;
              const tierString = 
                pubTier === 1
                  ? 'tier1'
                  : pubTier === 2
                  ? 'tier2'
                  : pubTier === 3
                  ? 'tier3'
                  : pubTier === 4
                  ? 'tier4'
                  : 'tier1'; // Default to tier1 if tier is invalid

              allPublications.push({
                id: `${pr._id}-${index}`,
                title: pr.title,
                url: pub.url,
                domain,
                status: 'published',
                publishedAt: typeof pub.publishedDate === 'string' 
                  ? pub.publishedDate 
                  : pub.publishedDate instanceof Date 
                    ? pub.publishedDate.toISOString()
                    : pr.updatedAt,
                prTitle: pr.title,
                prId: pr._id,
                channel: pub.outlet,
                tier: tierString,
                industry: pr.contactInfo?.company || 'General',
                location: 'Global',
                metrics: {
                  views,
                  clicks,
                  shares,
                  comments: 0,
                  engagement: Math.round(engagement * 10) / 10
                },
                author: pr.author?.fullName || 'Unknown',
                tags: pr.targetAudience?.slice(0, 3) || [],
                isFavorite: false,
                lastUpdated: pr.updatedAt
              });
            });
          } else {
            // Fallback: If no analytics data, try to fetch it (but only for a few PRs)
            // This is a fallback for older PRs that might not have analytics data
            console.log(`PR ${pr._id} has no analytics.publications data`);
          }
        });

        setPublications(allPublications);
        setLoadingProgress(100);
      } catch (err: any) {
        console.error('Error loading publications:', err);
        setError(err.message || 'Failed to load publications');
      } finally {
        setLoading(false);
        setLoadingProgress(0);
      }
    };

    fetchPublications();
  }, []);

  // Memoized filtered publications - optimized filtering
  const filteredPublications = useMemo(() => {
    let filtered = publications;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(pub => 
        pub.title.toLowerCase().includes(searchLower) ||
        pub.domain.toLowerCase().includes(searchLower) ||
        pub.channel.toLowerCase().includes(searchLower) ||
        pub.tags.some(tag => tag.toLowerCase().includes(searchLower))
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(pub => pub.status === statusFilter);
    }

    if (tierFilter !== 'all') {
      filtered = filtered.filter(pub => pub.tier === tierFilter);
    }

    if (industryFilter !== 'all') {
      filtered = filtered.filter(pub => pub.industry === industryFilter);
    }

    return filtered;
  }, [searchTerm, statusFilter, tierFilter, industryFilter, publications]);

  // Memoized statistics - calculate once and reuse
  const stats = useMemo(() => {
    const totalPublications = publications.length;
    const published = publications.filter(p => p.status === 'published').length;
    const totalViews = publications.reduce((sum, p) => sum + p.metrics.views, 0);
    
    // Calculate average engagement only for published publications
    const publishedPublications = publications.filter(p => p.status === 'published');
    const avgEngagement = publishedPublications.length > 0
      ? Math.round(
          publishedPublications.reduce((sum, p) => sum + p.metrics.engagement, 0) / 
          publishedPublications.length
        )
      : 0;

    return { totalPublications, published, totalViews, avgEngagement };
  }, [publications]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [searchTerm, statusFilter, tierFilter, industryFilter]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'published': return <CheckCircleIcon style={{ color: '#4CAF50' }} />;
      case 'scheduled': return <ScheduleIcon style={{ color: '#FF9800' }} />;
      case 'pending': return <PendingIcon style={{ color: '#2196F3' }} />;
      case 'rejected': return <ErrorIcon style={{ color: '#F44336' }} />;
      default: return <PendingIcon style={{ color: '#9E9E9E' }} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published': return '#4CAF50';
      case 'scheduled': return '#FF9800';
      case 'pending': return '#2196F3';
      case 'rejected': return '#F44336';
      default: return '#9E9E9E';
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'tier1': return '#4CAF50';
      case 'tier2': return '#2196F3';
      case 'tier3': return '#FF9800';
      case 'tier4': return '#9E9E9E';
      default: return '#9E9E9E';
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, publication: Publication) => {
    setAnchorEl(event.currentTarget);
    setSelectedPublication(publication);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPublication(null);
  };

  const handleViewPublication = () => {
    setViewDialogOpen(true);
    setAnchorEl(null); // Close menu but keep selectedPublication for dialog
  };

  const handleOpenLink = (url: string) => {
    window.open(url, '_blank');
  };

  const handleToggleFavorite = useCallback((publicationId: string) => {
    setPublications(prevPublications => 
      prevPublications.map(pub => 
        pub.id === publicationId 
          ? { ...pub, isFavorite: !pub.isFavorite }
          : pub
      )
    );
  }, []);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Container maxWidth="xl" style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <Typography variant="h4" style={{ fontWeight: 'bold', color: '#0927eb', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <PublicIcon style={{ fontSize: '32px' }} />
          Publications
        </Typography>
        <Typography variant="body1" style={{ color: '#666' }}>
          Track and monitor your published press releases
        </Typography>
      </div>

      {/* Stats Cards */}
      <Grid container spacing={3} style={{ marginBottom: '32px' }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card style={{ borderRadius: '12px' }}>
            <CardContent>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <Typography variant="h4" style={{ fontWeight: 'bold', color: '#0927eb' }}>
                    {stats.totalPublications}
                  </Typography>
                  <Typography variant="body2" style={{ color: '#666' }}>
                    Total Publications
                  </Typography>
                </div>
                <PublicIcon style={{ fontSize: '40px', color: '#0927eb', opacity: 0.3 }} />
              </div>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card style={{ borderRadius: '12px' }}>
            <CardContent>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <Typography variant="h4" style={{ fontWeight: 'bold', color: '#4CAF50' }}>
                    {stats.published}
                  </Typography>
                  <Typography variant="body2" style={{ color: '#666' }}>
                    Published
                  </Typography>
                </div>
                <CheckCircleIcon style={{ fontSize: '40px', color: '#4CAF50', opacity: 0.3 }} />
              </div>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card style={{ borderRadius: '12px' }}>
            <CardContent>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <Typography variant="h4" style={{ fontWeight: 'bold', color: '#FF9800' }}>
                    {stats.totalViews.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" style={{ color: '#666' }}>
                    Total Views
                  </Typography>
                </div>
                <VisibilityIcon style={{ fontSize: '40px', color: '#FF9800', opacity: 0.3 }} />
              </div>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card style={{ borderRadius: '12px' }}>
            <CardContent>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <Typography variant="h4" style={{ fontWeight: 'bold', color: '#2196F3' }}>
                    {stats.avgEngagement}%
                  </Typography>
                  <Typography variant="body2" style={{ color: '#666' }}>
                    Avg Engagement
                  </Typography>
                </div>
                <TrendingUpIcon style={{ fontSize: '40px', color: '#2196F3', opacity: 0.3 }} />
              </div>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {loading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress variant="determinate" value={loadingProgress} />
          <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center', color: '#666' }}>
            Loading publications... {loadingProgress}%
          </Typography>
        </Box>
      )}

      {error && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </Box>
      )}

      {/* Filters and Search */}
      <Paper style={{ padding: '24px', marginBottom: '24px', borderRadius: '12px' }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="Search publications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="published">Published</MenuItem>
                <MenuItem value="scheduled">Scheduled</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Tier</InputLabel>
              <Select
                value={tierFilter}
                onChange={(e) => setTierFilter(e.target.value)}
                label="Tier"
              >
                <MenuItem value="all">All Tiers</MenuItem>
                <MenuItem value="tier1">Tier 1</MenuItem>
                <MenuItem value="tier2">Tier 2</MenuItem>
                <MenuItem value="tier3">Tier 3</MenuItem>
                <MenuItem value="tier4">Tier 4</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Industry</InputLabel>
              <Select
                value={industryFilter}
                onChange={(e) => setIndustryFilter(e.target.value)}
                label="Industry"
              >
                <MenuItem value="all">All Industries</MenuItem>
                <MenuItem value="Technology">Technology</MenuItem>
                <MenuItem value="Healthcare">Healthcare</MenuItem>
                <MenuItem value="Energy">Energy</MenuItem>
                <MenuItem value="Finance">Finance</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="outlined"
              startIcon={<AnalyticsIcon />}
              fullWidth
              onClick={() => navigate('/analytics')}
              style={{
                borderColor: '#000000',
                color: '#000000'
              }}
            >
              View Analytics
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Publications Table */}
      <TableContainer component={Paper} style={{ borderRadius: '12px' }}>
        <Table>
          <TableHead>
            <TableRow style={{ backgroundColor: '#f5f5f5' }}>
              <TableCell style={{ fontWeight: 'bold' }}>Publication</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Channel</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Tier</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Views</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Engagement</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Published</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPublications
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((publication) => (
                <TableRow key={publication.id} hover>
                  <TableCell>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <Typography variant="subtitle2" style={{ fontWeight: 'bold', flex: 1 }}>
                          {publication.title}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleFavorite(publication.id)}
                        >
                          {publication.isFavorite ? (
                            <StarIcon style={{ color: '#FF9800', fontSize: '20px' }} />
                          ) : (
                            <StarBorderIcon style={{ color: '#666', fontSize: '20px' }} />
                          )}
                        </IconButton>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                        <LanguageIcon style={{ fontSize: '14px', color: '#666' }} />
                        <Typography variant="body2" style={{ color: '#666' }}>
                          {publication.domain}
                        </Typography>
                      </div>
                      <div>
                        {publication.tags.map((tag, index) => (
                          <Chip
                            key={index}
                            label={tag}
                            size="small"
                            style={{ marginRight: '4px', marginBottom: '2px' }}
                          />
                        ))}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                        {publication.channel}
                      </Typography>
                      <Typography variant="body2" style={{ color: '#666' }}>
                        {publication.location}
                      </Typography>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`Tier ${publication.tier.slice(-1)}`}
                      style={{
                        backgroundColor: getTierColor(publication.tier),
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(publication.status)}
                      label={publication.status.charAt(0).toUpperCase() + publication.status.slice(1)}
                      style={{
                        backgroundColor: getStatusColor(publication.status),
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <VisibilityIcon style={{ fontSize: '16px', color: '#666', marginRight: '4px' }} />
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                        {publication.metrics.views.toLocaleString()}
                      </Typography>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <TrendingUpIcon style={{ fontSize: '16px', color: '#666', marginRight: '4px' }} />
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold', color: publication.metrics.engagement > 5 ? '#4CAF50' : '#FF9800' }}>
                        {publication.metrics.engagement.toFixed(1)}%
                      </Typography>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <CalendarIcon style={{ fontSize: '16px', color: '#666' }} />
                      {publication.publishedAt ? new Date(publication.publishedAt).toLocaleDateString() : 'N/A'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {publication.url && (
                        <Tooltip title="Open Link">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenLink(publication.url)}
                          >
                            <OpenInNewIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <IconButton
                        onClick={(e) => handleMenuClick(e, publication)}
                        size="small"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            {!loading && filteredPublications.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" style={{ padding: '40px' }}>
                  <Typography variant="body1" style={{ color: '#999' }}>
                    {publications.length === 0 
                      ? 'No publications found. Your published press releases will appear here.'
                      : 'No publications match your filters.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredPublications.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewPublication}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        {selectedPublication?.url && (
          <MenuItem onClick={() => {
            handleOpenLink(selectedPublication.url);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <OpenInNewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Open Link</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => {
          navigate('/analytics');
          handleMenuClose();
        }}>
          <ListItemIcon>
            <AnalyticsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Analytics</ListItemText>
        </MenuItem>
      </Menu>

      {/* View Publication Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false);
          setSelectedPublication(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" style={{ fontWeight: 'bold' }}>
            {selectedPublication?.title}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedPublication && (
            <div style={{ marginTop: '16px' }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                    Publication Details
                  </Typography>
                  <div style={{ marginBottom: '16px' }}>
                    <Typography variant="body2" style={{ color: '#666', marginBottom: '4px' }}>
                      Channel
                    </Typography>
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                      {selectedPublication.channel}
                    </Typography>
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <Typography variant="body2" style={{ color: '#666', marginBottom: '4px' }}>
                      Domain
                    </Typography>
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                      {selectedPublication.domain}
                    </Typography>
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <Typography variant="body2" style={{ color: '#666', marginBottom: '4px' }}>
                      Author
                    </Typography>
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                      {selectedPublication.author}
                    </Typography>
                  </div>
                  {selectedPublication.url && (
                    <div style={{ marginBottom: '16px' }}>
                      <Typography variant="body2" style={{ color: '#666', marginBottom: '4px' }}>
                        URL
                      </Typography>
                      <Link href={selectedPublication.url} target="_blank" rel="noopener noreferrer">
                        {selectedPublication.url}
                      </Link>
                    </div>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                    Status & Tier
                  </Typography>
                  <div style={{ marginBottom: '16px' }}>
                    <Chip
                      icon={getStatusIcon(selectedPublication.status)}
                      label={selectedPublication.status.charAt(0).toUpperCase() + selectedPublication.status.slice(1)}
                      style={{
                        backgroundColor: getStatusColor(selectedPublication.status),
                        color: 'white',
                        fontWeight: 'bold',
                        marginRight: '8px'
                      }}
                    />
                    <Chip
                      label={`Tier ${selectedPublication.tier.slice(-1)}`}
                      style={{
                        backgroundColor: getTierColor(selectedPublication.tier),
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <Typography variant="body2" style={{ color: '#666', marginBottom: '4px' }}>
                      Published Date
                    </Typography>
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                      {selectedPublication.publishedAt ? new Date(selectedPublication.publishedAt).toLocaleString() : 'Not published yet'}
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                    Performance Metrics
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Paper style={{ padding: '16px', textAlign: 'center' }}>
                        <Typography variant="h6" style={{ fontWeight: 'bold', color: '#0927eb' }}>
                          {selectedPublication.metrics.views.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" style={{ color: '#666' }}>
                          Views
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={3}>
                      <Paper style={{ padding: '16px', textAlign: 'center' }}>
                        <Typography variant="h6" style={{ fontWeight: 'bold', color: '#4CAF50' }}>
                          {selectedPublication.metrics.clicks.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" style={{ color: '#666' }}>
                          Clicks
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={3}>
                      <Paper style={{ padding: '16px', textAlign: 'center' }}>
                        <Typography variant="h6" style={{ fontWeight: 'bold', color: '#FF9800' }}>
                          {selectedPublication.metrics.shares}
                        </Typography>
                        <Typography variant="body2" style={{ color: '#666' }}>
                          Shares
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={3}>
                      <Paper style={{ padding: '16px', textAlign: 'center' }}>
                        <Typography variant="h6" style={{ fontWeight: 'bold', color: '#2196F3' }}>
                          {selectedPublication.metrics.engagement}%
                        </Typography>
                        <Typography variant="body2" style={{ color: '#666' }}>
                          Engagement
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                    Tags
                  </Typography>
                  <div>
                    {selectedPublication.tags.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag}
                        size="small"
                        style={{ marginRight: '8px', marginBottom: '4px' }}
                      />
                    ))}
                  </div>
                </Grid>
              </Grid>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setViewDialogOpen(false);
            setSelectedPublication(null);
          }}>
            Close
          </Button>
          {selectedPublication?.url && (
            <Button
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              onClick={() => handleOpenLink(selectedPublication.url)}
            >
              Open Link
            </Button>
          )}
          <Button
            variant="contained"
            onClick={() => {
              setViewDialogOpen(false);
              navigate('/analytics');
            }}
            style={{
              backgroundColor: '#000000',
              color: 'white'
            }}
          >
            View Analytics
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Publications;